//
// ReputationBadge
//
// Small tier badge shown in the address page header (and inside the QNS
// domain page score card). Displays `{tier} · {score}` with color-coded
// tier scheme; hoverable tooltip shows the score range + penalty note.
//
// Hidden when:
//   - Feature master switch OFF
//   - Badge sub-switch OFF
//   - Address input is invalid (defensive; parent should already filter)
//   - Score is loading AND we don't have any cached data yet
//
// Never blocks the page — if reputation is unavailable, badge just doesn't
// render. Parent gets `null`.
//
import { Badge, Box, Tooltip, VStack, Text, chakra } from '@chakra-ui/react';
import React from 'react';

import { getEnvValue } from 'configs/app/utils';
import useReputationScore from 'lib/api/useReputationScore';
import Skeleton from 'ui/shared/chakra/Skeleton';

import { getTierDotScheme, getTierRangeLabel } from './ReputationTierColors';

const BADGE_ENABLED = getEnvValue('NEXT_PUBLIC_REPUTATION_BADGE_ENABLED') !== 'false';

interface Props {
  className?: string;
  addressOrDomain: string;
  isLoading?: boolean;
}

const ReputationBadge = ({ addressOrDomain, className, isLoading: parentIsLoading }: Props) => {
  const { data, isPending } = useReputationScore(addressOrDomain);

  if (!BADGE_ENABLED) return null;

  if (parentIsLoading || isPending) {
    return <Skeleton className={ className } height="24px" width="90px" borderRadius="md"/>;
  }

  if (!data) return null;

  const dotScheme = getTierDotScheme(data.tier);
  const range = getTierRangeLabel(data.tier);

  const tooltipLabel = (
    <VStack align="start" spacing={ 1.5 } py={ 1 }>
      <Text fontSize="sm" fontWeight={ 700 } color="white">
        QIE Reputation Score
      </Text>
      <Text fontSize="xs" color="whiteAlpha.900">
        Score: <chakra.span fontWeight={ 600 }>{ data.score.toFixed(2) } / 100</chakra.span>
      </Text>
      <Text fontSize="xs" color="whiteAlpha.900">
        Tier: <chakra.span fontWeight={ 600 }>{ data.tier }{ range ? ` (${ range })` : '' }</chakra.span>
      </Text>
      { data.penalty > 0 && (
        <Text fontSize="xs" color="red.200" fontWeight={ 600 }>
          Penalty applied: −{ data.penalty }
        </Text>
      ) }
      { data.unavailableFactors.length > 0 && (
        <Text fontSize="xs" color="orange.200">
          Some factors unavailable — score may be understated.
        </Text>
      ) }
    </VStack>
  );

  return (
    <Tooltip
      label={ tooltipLabel }
      placement="top"
      bg="gray.800"
      color="white"
      hasArrow
      openDelay={ 100 }
    >
      <Badge
        className={ className }
        fontSize="sm"
        fontWeight={ 700 }
        px={ 2.5 }
        py={ 1 }
        borderRadius="md"
        display="inline-flex"
        alignItems="center"
        gap={ 2 }
        cursor="default"
      >
        { /* Tier dot — provides visual differentiation without fighting theme */ }
        <Box
          w="8px"
          h="8px"
          borderRadius="full"
          bg={ `${ dotScheme }.400` }
          flexShrink={ 0 }
        />
        <chakra.span>{ data.tier }</chakra.span>
        <chakra.span opacity={ 0.7 }>·</chakra.span>
        <chakra.span>{ Math.round(data.score) }</chakra.span>
      </Badge>
    </Tooltip>
  );
};

export default React.memo(chakra(ReputationBadge));